import React, { useState, useEffect, useMemo } from 'react';
import { StockData } from '../services/googleSheetsService';
import { AlphaPickService } from '../services/alphaPickService';
import LoadingSpinner from './LoadingSpinner';
import { TrendingUp, TrendingDown, Star, Calendar, ArrowUp, ArrowDown, ArrowUpDown, BookmarkPlus, BookmarkCheck, Loader2 } from 'lucide-react';
import TradingViewModal from './TradingViewModal';
import StockLogo from './StockLogo';
import { useWatchlistStore } from '../store/watchlistStore';
import Sparkline from './Sparkline';
import { apiFetch } from '../lib/api';

interface RatingOption { value: string; label: string }

interface GoogleSheetsTableProps {
  reloadKey?: number;
  ratingFilter: string;
  onRatingOptionsChange: (options: RatingOption[]) => void;
}

type SortKey = 'company' | 'picked' | 'return' | 'sector' | 'rating' | 'holdingPercent';

export const GoogleSheetsTable: React.FC<GoogleSheetsTableProps> = ({ reloadKey, ratingFilter, onRatingOptionsChange }) => {
  const [data, setData] = useState<StockData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>('picked');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addingSymbols, setAddingSymbols] = useState<Set<string>>(new Set());
  const [sparklines, setSparklines] = useState<Record<string, number[]>>({});

  const alphaPickService = new AlphaPickService();
  const { items: watchlistItems, addItem } = useWatchlistStore();

  useEffect(() => {
    const ac = new AbortController()
    apiFetch('/api/alpha-picks/sparklines', { signal: ac.signal })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.sparklines) setSparklines(d.sparklines); })
      .catch(() => {})
    return () => ac.abort()
  }, []);

  const watchlistSymbols = useMemo(
    () => new Set(watchlistItems.map(i => i.symbol)),
    [watchlistItems]
  );

  const handleAddToWatchlist = async (e: React.MouseEvent, stock: StockData) => {
    e.stopPropagation();
    const sym = stock.symbol;
    if (watchlistSymbols.has(sym) || addingSymbols.has(sym)) return;
    setAddingSymbols(prev => new Set(prev).add(sym));
    try {
      const portType = sym.endsWith('.BK') ? 'th_stock' : 'us_stock';
      await addItem(sym, stock.company || sym, portType);
    } catch {
      // error already toasted by store
    } finally {
      setAddingSymbols(prev => { const next = new Set(prev); next.delete(sym); return next; });
    }
  };

  const transformAlphaPicksToStockData = (alphaPicks: any[]): StockData[] => {
    return alphaPicks.map(pick => ({
      company: pick.company || '',
      symbol: pick.symbol || '',
      picked: pick.picked || '',
      return: pick.return_val || '',
      sector: pick.sector || '',
      rating: pick.rating || '',
      holdingPercent: pick.holding_percent || '',
      high52w: pick.high_52w ?? null,
      low52w: pick.low_52w ?? null,
      currentPrice: pick.current_price ?? null,
      pickedPrice: pick.picked_price ?? null,
    }));
  };

  useEffect(() => {
    const ac = new AbortController()
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await apiFetch('/api/alpha-picks', { signal: ac.signal })
        if (!res.ok) throw new Error(`Failed to fetch alpha picks: ${res.statusText}`)
        const result = await res.json()
        const stockData = transformAlphaPicksToStockData(result.data || [])
        setData(stockData);
        setError(null);
      } catch (err) {
        if ((err as any)?.name === 'AbortError') return
        setError(err instanceof Error ? err.message : 'Failed to fetch alpha picks from database');
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };
    fetchData();
    return () => ac.abort()
  }, [reloadKey]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3 h-3 flex-shrink-0 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0" /> : <ArrowDown className="w-3 h-3 flex-shrink-0" />;
  };

  const ColHeader = ({ label, col, align = 'left' }: { label: string; col: SortKey; align?: 'left' | 'right' }) => (
    <button
      onClick={() => handleSort(col)}
      className={`flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest select-none transition-colors w-full
        ${align === 'right' ? 'justify-end' : ''}
        ${sortBy === col ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
    >
      {label}
      <SortIcon col={col} />
    </button>
  );

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) return date.toLocaleDateString('en-GB');
    } catch {
      // fall through
    }
    const dateOnlyStr = dateStr.split(' ')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnlyStr)) {
      const [y, m, d] = dateOnlyStr.split('-');
      return `${d}/${m}/${y}`;
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateOnlyStr)) {
      const [m, d, y] = dateOnlyStr.split('/');
      return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
    return dateOnlyStr;
  };

  const ratingRank = (r: string): number => {
    const n = r.trim().toLowerCase();
    if (n === 'strong buy') return 4;
    if (n === 'buy') return 3;
    if (n === 'hold') return 2;
    if (n === 'sell') return 1;
    const num = parseFloat(r);
    return isNaN(num) ? 0 : num;
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const aVal = a[sortBy as keyof StockData] || '';
      const bVal = b[sortBy as keyof StockData] || '';
      if (sortBy === 'picked') {
        return sortDir === 'asc'
          ? new Date(aVal as string).getTime() - new Date(bVal as string).getTime()
          : new Date(bVal as string).getTime() - new Date(aVal as string).getTime();
      }
      if (sortBy === 'rating') {
        const aNum = ratingRank(aVal as string);
        const bNum = ratingRank(bVal as string);
        return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
      }
      if (['return', 'holdingPercent'].includes(sortBy)) {
        const aNum = parseFloat((aVal as string).replace(/[%+]/g, ''));
        const bNum = parseFloat((bVal as string).replace(/[%+]/g, ''));
        if (isNaN(aNum) && isNaN(bNum)) return 0;
        if (isNaN(aNum)) return 1;
        if (isNaN(bNum)) return -1;
        return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
      }
      return sortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, sortBy, sortDir]);

  const ratingOptions = useMemo(() => {
    const set = new Set<string>();
    data.forEach(s => { if (s.rating?.trim()) set.add(s.rating.trim()); });
    const opts: RatingOption[] = [
      { value: '', label: 'All Ratings' },
      ...Array.from(set).sort().map(r => ({ value: r, label: r }))
    ];
    return opts;
  }, [data]);

  useEffect(() => {
    onRatingOptionsChange(ratingOptions);
  }, [ratingOptions]);

  const filteredData = useMemo(() => {
    if (!ratingFilter) return sortedData;
    return sortedData.filter(s => s.rating?.trim() === ratingFilter);
  }, [sortedData, ratingFilter]);

  const getRatingChip = (rating: string) => {
    const normalized = rating.trim().toLowerCase();
    let color = 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
    let display = rating;
    if (normalized === 'hold') {
      color = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      display = 'Hold';
    } else if (normalized === 'strong buy') {
      color = 'bg-green-800 text-green-100 dark:bg-green-900 dark:text-green-200';
      display = 'Strong Buy';
    } else if (normalized === 'buy') {
      color = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      display = 'Buy';
    } else {
      const num = parseFloat(rating);
      if (!isNaN(num)) {
        if (num < 2) color = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        else if (num < 4) color = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
        else color = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
        display = num.toFixed(1);
      }
    }
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold w-fit ${color}`}>
        {display}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-loss">Error: {error}</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        <Star className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No alpha picks available. Click "Reload" to fetch fresh data from Google Sheets.</p>
      </div>
    );
  }

  const GRID = 'grid-cols-[1fr_96px_96px_80px_80px_80px_80px_96px_64px_20px]';

  return (
    <div className="overflow-x-auto">
      {/* Header */}
      <div className={`grid ${GRID} items-center px-6 py-4 gap-x-6 table-head`}>
        <ColHeader label="Company" col="company" />
        <ColHeader label="Rating" col="rating" align="right" />
        <ColHeader label="Picked" col="picked" align="right" />
        <div className="flex justify-end text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 select-none">At Pick</div>
        <div className="flex justify-end text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 select-none">Curr Price</div>
        <ColHeader label="Return" col="return" align="right" />
        <div className="flex justify-end text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 select-none">YTD</div>
        <div className="flex justify-end text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 select-none">52W</div>
        <ColHeader label="Holding" col="holdingPercent" align="right" />
        <span />
      </div>

      {/* Rows */}
      {filteredData.map((stock, index) => {
        const returnNum = stock.return ? parseFloat(stock.return.replace(/[%+]/g, '')) : NaN;
        const isPos = !isNaN(returnNum) && returnNum >= 0;
        const isNeg = !isNaN(returnNum) && returnNum < 0;

        return (
          <div
            key={`${stock.symbol}-${index}`}
            onClick={() => { setSelectedSymbol(stock.symbol); setIsModalOpen(true); }}
            className={`grid ${GRID} items-center px-6 py-3.5 gap-x-6
              border-b border-gray-100 dark:border-gray-700/40
              border-l-2 border-l-transparent hover:border-l-amber-400
              hover:bg-amber-50/30 dark:hover:bg-amber-900/10
              transition-colors duration-150 cursor-pointer`}
          >
            {/* Company */}
            <div className="flex items-center gap-2.5 min-w-0">
              <StockLogo symbol={stock.symbol} className="flex-shrink-0" />
              <div className="min-w-0 flex flex-col gap-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-bold text-gray-900 dark:text-white flex-shrink-0">{stock.symbol}</span>
                  <p className="text-xs text-gray-400 truncate leading-tight">{stock.company}</p>
                </div>
                {stock.sector && (
                  <span className="text-[9px] px-1 py-px rounded bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-400 w-fit leading-tight">
                    {stock.sector}
                  </span>
                )}
              </div>
            </div>

            {/* Rating */}
            <div className="flex justify-end">
              {stock.rating ? getRatingChip(stock.rating) : <span className="text-xs text-gray-300 dark:text-gray-400">—</span>}
            </div>

            {/* Picked */}
            <div className="flex items-center justify-end gap-1 text-xs text-gray-400 dark:text-gray-400">
              {stock.picked ? (
                <>
                  <Calendar className="w-3 h-3 flex-shrink-0" />
                  <span>{formatDate(stock.picked)}</span>
                </>
              ) : <span className="text-gray-300 dark:text-gray-400">—</span>}
            </div>

            {/* At Pick */}
            <div className="flex justify-end text-xs tabular-nums text-gray-500 dark:text-gray-400">
              {stock.pickedPrice != null
                ? `${stock.symbol.endsWith('.BK') ? '฿' : '$'}${stock.pickedPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : <span className="text-gray-300 dark:text-gray-400">—</span>}
            </div>

            {/* Price (current) */}
            <div className="flex justify-end text-xs font-bold text-gray-900 dark:text-white tabular-nums">
              {stock.currentPrice != null
                ? `${stock.symbol.endsWith('.BK') ? '฿' : '$'}${stock.currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : <span className="text-gray-300 dark:text-gray-400">—</span>}
            </div>

            {/* Return */}
            <div className="flex justify-end">
              {stock.return ? (
                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-md
                  ${isPos ? 'bg-gradient-to-r from-green-50 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 text-gain' : ''}
                  ${isNeg ? 'bg-gradient-to-r from-rose-50 to-pink-100 dark:from-rose-900/30 dark:to-pink-900/30 text-loss' : ''}
                  ${isNaN(returnNum) ? 'text-gray-400' : ''}`}
                >
                  {isPos && <TrendingUp className="w-3 h-3" />}
                  {isNeg && <TrendingDown className="w-3 h-3" />}
                  {stock.return}
                </span>
              ) : <span className="text-xs text-gray-300 dark:text-gray-400">—</span>}
            </div>

            {/* YTD Sparkline */}
            <div className="flex justify-end">
              {sparklines[stock.symbol] ? (
                <Sparkline data={sparklines[stock.symbol]} positive={isPos || !isNeg} />
              ) : (
                <span className="text-xs text-gray-300 dark:text-gray-400">—</span>
              )}
            </div>

            {/* 52W bar */}
            <div className="flex justify-end">
              {stock.high52w != null && stock.low52w != null && stock.currentPrice != null ? (
                (() => {
                  const pct = Math.min(100, Math.max(2, ((stock.currentPrice - stock.low52w) / (stock.high52w - stock.low52w)) * 100));
                  const sym = stock.symbol.endsWith('.BK') ? '฿' : '$';
                  return (
                    <div className="w-full">
                      <div className="relative h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-visible mb-1">
                        <div className={`h-full rounded-full ${isPos ? 'bg-gain-gradient' : 'bg-loss-gradient'}`} style={{ width: `${pct}%` }} />
                        <div className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white border-2 border-gray-400 dark:border-gray-300 shadow-sm" style={{ left: `calc(${pct}% - 4px)` }} />
                      </div>
                      <div className="flex justify-between text-[10px] text-gray-400 tabular-nums">
                        <span>{sym}{stock.low52w.toFixed(0)}</span>
                        <span>{sym}{stock.high52w.toFixed(0)}</span>
                      </div>
                    </div>
                  );
                })()
              ) : <span className="text-xs text-gray-300 dark:text-gray-400">—</span>}
            </div>

            {/* Holding */}
            <div className="flex justify-end text-xs font-medium text-purple-600 dark:text-purple-400">
              {stock.holdingPercent || <span className="text-gray-300 dark:text-gray-400">—</span>}
            </div>

            {/* Add to Watchlist */}
            {watchlistSymbols.has(stock.symbol) ? (
              <BookmarkCheck className="w-4 h-4 text-indigo-400 dark:text-indigo-500" />
            ) : addingSymbols.has(stock.symbol) ? (
              <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
            ) : (
              <button
                onClick={(e) => handleAddToWatchlist(e, stock)}
                title="Add to Watchlist"
                className="p-0.5 rounded text-gray-300 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors"
              >
                <BookmarkPlus className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      })}

      <TradingViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        symbol={selectedSymbol}
      />
    </div>
  );
};

export default GoogleSheetsTable;
